import axiosInstance from '../../../config/axios';
import {
  InventoryList,
  InventoryItem,
  CountSession,
  SessionItem,
  CountResult,
  SkipResult,
  SessionResult,
} from '../types';

/**
 * Service for inventory management and stock counting operations.
 * Admin endpoints handle list/item/session CRUD.
 * Counting flow endpoints handle the station-facing count process.
 */
class InventoryService {
  private static readonly ADMIN_BASE = '/api/admin/inventory';
  private static readonly BASE = '/api/inventory';

  static async getLists(): Promise<InventoryList[]> {
    const response = await axiosInstance.get(`${this.ADMIN_BASE}/lists/`);
    return response.data;
  }

  static async getListDetail(listId: number): Promise<InventoryList> {
    const response = await axiosInstance.get(`${this.ADMIN_BASE}/lists/${listId}/`);
    return response.data;
  }

  static async getItems(params?: {
    list?: number;
    supplier?: number;
    status?: string;
  }): Promise<InventoryItem[]> {
    const searchParams = new URLSearchParams();
    if (params?.list) searchParams.append('list', String(params.list));
    if (params?.supplier) searchParams.append('supplier', String(params.supplier));
    if (params?.status) searchParams.append('status', params.status);

    const query = searchParams.toString();
    const url = `${this.ADMIN_BASE}/items/${query ? `?${query}` : ''}`;
    const response = await axiosInstance.get(url);
    return response.data;
  }

  static async createItem(data: Partial<InventoryItem>): Promise<InventoryItem> {
    const response = await axiosInstance.post(`${this.ADMIN_BASE}/items/`, data);
    return response.data;
  }

  static async updateItem(itemId: number, data: Partial<InventoryItem>): Promise<InventoryItem> {
    const response = await axiosInstance.patch(`${this.ADMIN_BASE}/items/${itemId}/`, data);
    return response.data;
  }

  static async getSessions(params?: {
    work_area?: number;
    status?: string;
  }): Promise<CountSession[]> {
    const searchParams = new URLSearchParams();
    if (params?.work_area) searchParams.append('work_area', String(params.work_area));
    if (params?.status) searchParams.append('status', params.status);

    const query = searchParams.toString();
    const url = `${this.ADMIN_BASE}/sessions/${query ? `?${query}` : ''}`;
    const response = await axiosInstance.get(url);
    return response.data;
  }

  static async getSessionDetail(sessionId: number): Promise<SessionResult> {
    const response = await axiosInstance.get(`${this.ADMIN_BASE}/sessions/${sessionId}/`);
    return response.data;
  }

  static async getLatestSessions(): Promise<CountSession[]> {
    const response = await axiosInstance.get(`${this.ADMIN_BASE}/sessions/latest/`);
    return response.data;
  }

  static async startSession(workAreaId: number): Promise<CountSession> {
    const response = await axiosInstance.post(`${this.BASE}/sessions/start/`, {
      work_area_id: workAreaId,
    });
    return response.data;
  }

  static async getSessionItems(sessionId: number): Promise<SessionItem[]> {
    const response = await axiosInstance.get(`${this.BASE}/sessions/${sessionId}/items/`);
    return response.data;
  }

  static async countItem(
    sessionId: number,
    itemId: number,
    quantity: number
  ): Promise<CountResult> {
    const response = await axiosInstance.post(`${this.BASE}/sessions/${sessionId}/count/`, {
      item_id: itemId,
      quantity,
    });
    return response.data;
  }

  static async skipItem(sessionId: number, itemId: number): Promise<SkipResult> {
    const response = await axiosInstance.post(`${this.BASE}/sessions/${sessionId}/skip/`, {
      item_id: itemId,
    });
    return response.data;
  }

  static async completeSession(sessionId: number): Promise<CountSession> {
    const response = await axiosInstance.post(`${this.BASE}/sessions/${sessionId}/complete/`);
    return response.data;
  }

  static async getSessionResult(sessionId: number): Promise<SessionResult> {
    const response = await axiosInstance.get(`${this.BASE}/sessions/${sessionId}/result/`);
    return response.data;
  }
}

export default InventoryService;
